import { notifyWarning } from '@/bugsnag';
import 'multiple-select/index.d.ts';

declare global {
    interface Window {
        multipleSelectInitSingle: typeof multipleSelectInitSingle;
        multipleSelectInitMultiple: typeof multipleSelectInitMultiple;
        multipleSelectRefresh: typeof multipleSelectRefresh;
        multipleSelectGetSelects: typeof multipleSelectGetSelects;
        multipleSelectSetSelects: typeof multipleSelectSetSelects;
        multipleSelectSetEnabled: typeof multipleSelectSetEnabled;
    }
}

export function multipleSelectInitSingle(selector: string, itemName: string) {
    window.$(selector).multipleSelect({
        width: '100%',
        single: true,
        placeholder: 'Select ' + itemName,
        hideOptgroupCheckboxes: true,

        // Recheck validation (and clear error) on selection
        onClick: function () {
            $(selector).valid();
        },
        formatNoMatchesFound: function () {
            return 'No ' + itemName + ' available';
        },
    });
    $(selector).multipleSelect('uncheckAll');
}
window.multipleSelectInitSingle = multipleSelectInitSingle;

export function multipleSelectInitMultiple(selector: string, actionGroup: string, action: string, shouldFilter: boolean) {
    return window.$(selector).multipleSelect({
        selectAll: true,
        width: '100%',
        placeholder: 'Select ' + actionGroup,
        hideOptgroupCheckboxes: true,
        filter: shouldFilter,
        formatAllSelected: function () {
            return 'All clients will ' + action;
        },
        formatNoMatchesFound: function () {
            return 'No Clients found. Please add an email, phone and client type.';
        },
    });
}
window.multipleSelectInitMultiple = multipleSelectInitMultiple;

// warning: if element is disabled this enables it
export function multipleSelectRefresh(selector: string) {
    $(selector).multipleSelect('refresh');
}
window.multipleSelectRefresh = multipleSelectRefresh;

export function multipleSelectGetSelects(selector: string): string[] {
    const $selector = $(selector);
    if ($selector.length === 0) {
        console.error(`Selector "${selector}" did not match any elements.`);
        notifyWarning(`Selector "${selector}" did not match any elements.`, 'multiple-select-scripts:multipleSelectGetSelects', {
            selector,
        });
        return [];
    }
    if (!$selector.data('multipleSelect')) {
        notifyWarning(
            `multipleSelect is not initialized for selector "${selector}".`,
            'multiple-select-scripts:multipleSelectGetSelects',
            { requiredFields: { selector } },
        );
        return [];
    }
    return $selector.is(':enabled') ? $selector.multipleSelect('getSelects') : [];
}
window.multipleSelectGetSelects = multipleSelectGetSelects;

export function multipleSelectSetEnabled(selector: string, enabled: boolean) {
    $(selector).multipleSelect(enabled ? 'enable' : 'disable');
}
window.multipleSelectSetEnabled = multipleSelectSetEnabled;

export interface Item {
    value: string;
    text: string;
}

export function multipleSelectSetSelects(selector: string, items: Item[], selected?: number | number[], disabled?: number[]) {
    const $selector = $(selector);
    const beforeHtml = $selector.html();
    $selector.html('');
    // selectorGroup is used to update the signer # that is shown (if applicable).
    // Currently, only the document dropdowns show the signer #
    const selectorGroup = $selector.data('id');

    // Each item (in items) contains the 'text' that should be displayed
    $.each(items, function (index, item) {
        $('<option />')
            .val(item.value)
            .html(
                item.text +
                    `<span class='signer-label' data-selection-order-group='${selectorGroup}' data-id='${item.value}'></span>`,
            )
            .appendTo($selector);
        // if disabled is an array, check if the index is in the array and if the item is selected.
        if (disabled && disabled.includes(index)) {
            const $option = $selector.find('option').eq(index);
            // don't actually disable the option if it's selected, add a class to prevent it from being selected
            $option.addClass('ms-custom-option-disabled');
        }
    });

    if (selected) {
        if (typeof selected === 'number') {
            selected = [selected];
        }
        $selector.val(selected.map(String));
    }

    if (disabled?.length) {
        // add a class to the selector to prevent select-all
        $selector.addClass('ms-custom-select-all-disabled');
    }

    // For performance reasons alone, only reinitialize the multipleSelect if the contents (i.e. html) have changed.
    // Otherwise, it's safe to always reinitialize the multipleSelect
    const afterHtml = $selector.html();
    if (beforeHtml !== afterHtml) {
        multipleSelectRefresh(selector);
    }

    if (selected) {
        $selector.multipleSelect('setSelects', selected);
    }
}
window.multipleSelectSetSelects = multipleSelectSetSelects;

window.ScriptRegistry?.register(window.ScriptRegistry.knownScripts.multipleSelectScripts);
